import mermaid, { type MermaidConfig } from "mermaid";

export type ThemePreset = "default" | "neutral" | "dark" | "forest" | "github" | "print";
export type SecurityLevel = "strict" | "loose" | "antiscript";
type MermaidTheme = NonNullable<MermaidConfig["theme"]>;

export type DiagramMetrics = {
  nodes: number;
  edges: number;
  lines: number;
  characters: number;
};

export type DiagramCheck = {
  id: string;
  label: string;
  koLabel: string;
  detail: string;
  koDetail: string;
  status: "pass" | "warn" | "fail";
};

export type RenderResult = {
  svg: string;
  diagramType: string;
  metrics: DiagramMetrics;
  checks: DiagramCheck[];
  renderedAt: string;
  error: string | null;
};

export const mermaidVersionLabel = "Mermaid v11.15";

const themeVariablesByPreset: Record<ThemePreset, Record<string, string | boolean>> = {
  default: {},
  neutral: {
    background: "#f8faf6",
    primaryColor: "#e8f1ff",
    primaryBorderColor: "#2158a6",
    primaryTextColor: "#0a2342",
    lineColor: "#253f67",
    secondaryColor: "#f3f7ff",
    tertiaryColor: "#ffffff"
  },
  dark: {
    darkMode: true,
    background: "#071a34",
    primaryColor: "#102b52",
    primaryBorderColor: "#58a6ff",
    primaryTextColor: "#f4fbff",
    lineColor: "#9fd7ff",
    secondaryColor: "#1c3b5f",
    tertiaryColor: "#0b223f"
  },
  forest: {
    primaryColor: "#e8f8ec",
    primaryBorderColor: "#237a48",
    primaryTextColor: "#0b3522",
    lineColor: "#2d6a4f",
    secondaryColor: "#d7f2e0",
    tertiaryColor: "#fbfff6"
  },
  github: {
    background: "#ffffff",
    primaryColor: "#f6f8fa",
    primaryBorderColor: "#0969da",
    primaryTextColor: "#24292f",
    lineColor: "#57606a",
    secondaryColor: "#ddf4ff",
    tertiaryColor: "#ffffff"
  },
  print: {
    background: "#ffffff",
    primaryColor: "#ffffff",
    primaryBorderColor: "#111827",
    primaryTextColor: "#111827",
    lineColor: "#111827",
    secondaryColor: "#f5f5f4",
    tertiaryColor: "#ffffff"
  }
};

const mermaidThemeByPreset: Record<ThemePreset, MermaidTheme> = {
  default: "default",
  neutral: "neutral",
  dark: "dark",
  forest: "forest",
  github: "base",
  print: "base"
};

export function extractMermaidCode(input: string): string {
  const fence = input.match(/```mermaid\s*([\s\S]*?)```/i);
  return (fence?.[1] ?? input).trim();
}

export function buildMarkdownBlock(code: string): string {
  return `\`\`\`mermaid\n${extractMermaidCode(code)}\n\`\`\``;
}

export function estimateMetrics(code: string): DiagramMetrics {
  const source = extractMermaidCode(code);
  const edgeMatches = source.match(/-->|---|==>|-.->|->>|-->>|-\)|--\)|\|\|--|}\|--|--\|/g);
  const labelMatches = source.match(/\b[A-Za-z][A-Za-z0-9_./ -]{1,40}\s*(?:\[|\(|\{|\[\(|\(\(|\[\[)/g);
  const participantMatches = source.match(/^\s*(?:participant|actor)\s+[\w -]+/gm);
  const entityMatches = source.match(/^\s*[A-Z][A-Z0-9_]+\s+\{/gm);
  const nodes = new Set<string>();

  for (const match of labelMatches ?? []) {
    nodes.add(match.replace(/[\[({]/g, "").trim());
  }
  for (const match of participantMatches ?? []) {
    nodes.add(match.replace(/^\s*(participant|actor)\s+/, "").trim());
  }
  for (const match of entityMatches ?? []) {
    nodes.add(match.replace("{", "").trim());
  }

  return {
    nodes: nodes.size,
    edges: edgeMatches?.length ?? 0,
    lines: source.split(/\r?\n/).filter((line) => line.trim().length > 0).length,
    characters: source.length
  };
}

function hasAccessibleTitle(code: string): boolean {
  return /^\s*accTitle\s*:/im.test(code);
}

function hasAccessibleDescription(code: string): boolean {
  return /^\s*accDescr\s*(?::|\{)/im.test(code);
}

function hasGitHubFence(input: string): boolean {
  return /```mermaid[\s\S]*?```/i.test(input);
}

function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function checkStatus(
  id: string,
  label: string,
  koLabel: string,
  detail: string,
  koDetail: string,
  status: DiagramCheck["status"]
): DiagramCheck {
  return { id, label, koLabel, detail, koDetail, status };
}

function createChecks(
  sourceInput: string,
  code: string,
  parsePass: boolean,
  renderPass: boolean,
  metrics: DiagramMetrics
): DiagramCheck[] {
  const checks: DiagramCheck[] = [
    checkStatus(
      "syntax",
      "Syntax",
      "문법",
      parsePass ? "Mermaid parser accepted the source." : "Mermaid parser reported an error.",
      parsePass ? "Mermaid 파서가 소스를 통과시켰습니다." : "Mermaid 파서가 오류를 보고했습니다.",
      parsePass ? "pass" : "fail"
    ),
    checkStatus(
      "render",
      "Diagram render",
      "렌더링",
      renderPass ? "SVG render completed in this browser." : "Render did not complete.",
      renderPass ? "브라우저에서 SVG 렌더링이 완료됐습니다." : "렌더링이 완료되지 않았습니다.",
      renderPass ? "pass" : "fail"
    ),
    checkStatus(
      "github",
      "GitHub Markdown block",
      "GitHub 마크다운",
      hasGitHubFence(sourceInput)
        ? "Input already includes a fenced mermaid block."
        : "Generated block is ready to copy.",
      hasGitHubFence(sourceInput)
        ? "입력에 Mermaid 코드 블록이 포함되어 있습니다."
        : "복사 가능한 Mermaid 코드 블록을 생성했습니다.",
      parsePass ? "pass" : "warn"
    ),
    checkStatus(
      "acctitle",
      "accTitle",
      "접근성 제목",
      hasAccessibleTitle(code)
        ? "Accessible SVG title is present."
        : "Add accTitle so screen readers and search tools receive a diagram title.",
      hasAccessibleTitle(code)
        ? "접근성 제목이 있습니다."
        : "스크린리더와 검색 도구를 위해 accTitle을 추가하세요.",
      hasAccessibleTitle(code) ? "pass" : "warn"
    ),
    checkStatus(
      "accdescr",
      "accDescr",
      "접근성 설명",
      hasAccessibleDescription(code)
        ? "Accessible SVG description is present."
        : "Add accDescr to explain the diagram in text.",
      hasAccessibleDescription(code)
        ? "접근성 설명이 있습니다."
        : "다이어그램을 텍스트로 설명하는 accDescr을 추가하세요.",
      hasAccessibleDescription(code) ? "pass" : "warn"
    ),
    checkStatus(
      "size",
      "Diagram size",
      "다이어그램 크기",
      metrics.nodes > 80 || metrics.edges > 120
        ? "Large diagrams can become hard to review in GitHub."
        : "Size is reasonable for Markdown review.",
      metrics.nodes > 80 || metrics.edges > 120
        ? "큰 다이어그램은 GitHub 리뷰에서 읽기 어려울 수 있습니다."
        : "Markdown 리뷰에 적당한 크기입니다.",
      metrics.nodes > 80 || metrics.edges > 120 ? "warn" : "pass"
    )
  ];
  return checks;
}

function themeConfig(theme: ThemePreset, securityLevel: SecurityLevel): MermaidConfig {
  return {
    startOnLoad: false,
    securityLevel,
    theme: mermaidThemeByPreset[theme],
    themeVariables: themeVariablesByPreset[theme]
  };
}

export async function renderMermaid(
  sourceInput: string,
  theme: ThemePreset,
  securityLevel: SecurityLevel
): Promise<RenderResult> {
  const code = extractMermaidCode(sourceInput);
  const metrics = estimateMetrics(code);
  const renderedAt = new Intl.DateTimeFormat("en", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(new Date());

  mermaid.initialize(themeConfig(theme, securityLevel));

  let parsePass = false;
  let diagramType = "unknown";

  try {
    const parseResult = await mermaid.parse(code, { suppressErrors: true });
    parsePass = Boolean(parseResult);
    try {
      diagramType = mermaid.detectType(code);
    } catch {
      diagramType = parsePass ? "detected" : "unknown";
    }
  } catch {
    parsePass = false;
  }

  if (!parsePass) {
    return {
      svg: "",
      diagramType,
      metrics,
      renderedAt,
      error: "Mermaid syntax did not pass validation.",
      checks: createChecks(sourceInput, code, false, false, metrics)
    };
  }

  try {
    const id = `diagramdock-${Date.now()}-${Math.round(Math.random() * 100000)}`;
    const { svg } = await mermaid.render(id, code);
    return {
      svg,
      diagramType,
      metrics,
      renderedAt,
      error: null,
      checks: createChecks(sourceInput, code, true, true, metrics)
    };
  } catch (error) {
    return {
      svg: "",
      diagramType,
      metrics,
      renderedAt,
      error: describeError(error),
      checks: createChecks(sourceInput, code, true, false, metrics)
    };
  }
}

export function svgSize(svg: string): { width: number; height: number } {
  const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
  const svgElement = doc.documentElement;
  const viewBox = svgElement.getAttribute("viewBox");

  if (viewBox) {
    const parts = viewBox.split(/\s+/).map((value) => Number(value));
    if (parts.length === 4 && parts.every((value) => Number.isFinite(value))) {
      return {
        width: Math.max(640, Math.round(parts[2])),
        height: Math.max(420, Math.round(parts[3]))
      };
    }
  }

  const width = Number.parseFloat(svgElement.getAttribute("width") ?? "");
  const height = Number.parseFloat(svgElement.getAttribute("height") ?? "");

  return {
    width: Number.isFinite(width) ? Math.max(640, Math.round(width)) : 1280,
    height: Number.isFinite(height) ? Math.max(420, Math.round(height)) : 720
  };
}
